import { EntryMethod } from 'components/enter/EntryMethod';
import { useSkipChooseYourPlan } from 'hooks/ab-test/useSkipChooseYourPlan';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { getChooseYourPlanBoostValue } from 'utils/ab-test/abTests';
import { ENTER_CTA_LINK, ENTER_FLOW_FIRST_STEP } from 'utils/cta/CTA';

export const UPGRADE_CTA_LINK = '/account/subscriptions/upgrade';

const DEFAULT_TEXT = 'Enter to Win';
const REFERRAL_TEXT = 'Claim 50% Off';
const UPGRADE_TEXT = 'Go Premium';

interface Options {
  /** Replaces the default text. Ignored during the referral flow unless overrideTextInReferralFlow is set. */
  overrideText?: string;
  overrideTextInReferralFlow?: boolean;
  overrideHref?: string;
}

interface CtaButtonTextAndHref {
  text: string;
  href: string;
  onClick: () => void;
  /** True when the user already has the thing the CTA sells, so nothing should be rendered. */
  hidden: boolean;
  /** False while the user, subscription or AB test state is still resolving. Callers should render nothing until
   * this is true, otherwise the text and href visibly change after hydration. */
  isLoaded: boolean;
}

/** The single source of truth for "where does the enter/upgrade button go, and what does it say". Aware of the
 * referral flow, the user's subscription tier and the checkout2026 AB test.
 *
 * Must be called from within an `observer()` component so the store reads stay reactive. */
export const useCtaButtonTextAndHref = (
  options: Options = {},
): CtaButtonTextAndHref => {
  const enterStore = useEnterStore();
  const userStore = useUserStore();
  const { overrideText, overrideTextInReferralFlow, overrideHref } = options;
  const { isTestGroup, isLoaded: abTestLoaded } = useSkipChooseYourPlan();

  const { subscriberStatus } = userStore.subscriptionStatusInfo;
  const isLoaded =
    abTestLoaded &&
    enterStore.initialised &&
    userStore.userInitialized &&
    // Subscriptions are only ever fetched for a logged in user
    (!userStore.currentUser || userStore.subscriptionsInitialized);

  const goToEnterFlow = () => {
    enterStore.setEntryMethod(EntryMethod.PremiumSubscription);
    enterStore.setBoosted(getChooseYourPlanBoostValue() === 'ORIGINALV2');
  };

  const goToPremiumBoostedEnterFlow = () => {
    enterStore.setEntryMethod(EntryMethod.PremiumSubscription);
    enterStore.setBoosted(true);
  };
  const noop = () => {};

  if (subscriberStatus === 'PREMIUM') {
    return {
      text: overrideText ?? DEFAULT_TEXT,
      href: overrideHref ?? ENTER_CTA_LINK,
      onClick: noop,
      hidden: true,
      isLoaded,
    };
  }

  if (subscriberStatus === 'FREEMIUM' || subscriberStatus === 'LITE') {
    return {
      text: UPGRADE_TEXT,
      href: overrideHref ?? UPGRADE_CTA_LINK,
      onClick: noop,
      hidden: false,
      isLoaded,
    };
  }

  if (enterStore.isReferralFlow) {
    return {
      text:
        overrideText && overrideTextInReferralFlow
          ? overrideText
          : REFERRAL_TEXT,
      href: overrideHref ?? ENTER_FLOW_FIRST_STEP,
      onClick: goToEnterFlow,
      hidden: false,
      isLoaded,
    };
  }

  // The test group never sees the plan picker, so they go straight into the premium enter flow
  if (isTestGroup) {
    return {
      text: overrideText ?? DEFAULT_TEXT,
      href: overrideHref ?? ENTER_FLOW_FIRST_STEP,
      onClick: goToPremiumBoostedEnterFlow,
      hidden: false,
      isLoaded,
    };
  }

  return {
    text: overrideText ?? DEFAULT_TEXT,
    href: overrideHref ?? ENTER_CTA_LINK,
    onClick: noop,
    hidden: false,
    isLoaded,
  };
};
